import React, { useState, useEffect, FormEvent } from 'react';
import DeviceSelectionScreen from './DeviceSelectionScreen/DeviceSelectionScreen';
import IntroContainer from '../IntroContainer/IntroContainer';
import MediaErrorSnackbar from './MediaErrorSnackbar/MediaErrorSnackbar';
import RoomNameScreen from './RoomNameScreen/RoomNameScreen';
import { useAppState } from '../../state';
import { useParams } from 'react-router-dom';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

export enum Steps {
  roomNameStep,
  deviceSelectionStep,
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#0A66C2',
    display: 'flex',
    fontFamily: 'Inter, sans-serif',
  },
  leftPanel: {
    width: '420px',
    minWidth: '420px',
    background: '#085299',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 40px',
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    background: '#f8faff',
  },
  logoImg: {
    height: '72px',
    width: 'auto',
    objectFit: 'contain',
    backgroundColor: 'white',
    borderRadius: '22px'
  },
  leftTagline: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
    marginBottom: '12px',
  },
  leftHeading: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1.3,
    margin: '0 0 16px',
    letterSpacing: '-0.5px',
  },
  leftBody: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '14px',
    lineHeight: 1.7,
    margin: 0,
  },
  leftFooter: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '12px',
    fontWeight: 500,
  },
  contentWrap: {
    width: '100%',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  iconCircle: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    background: 'rgba(239,68,68,0.10)',
    border: '1px solid rgba(239,68,68,0.18)',
  },
  heading: {
    color: '#0f172a',
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 10px',
    textAlign: 'center' as const,
    letterSpacing: '-0.4px',
  },
  subtext: {
    color: '#64748b',
    fontSize: '14px',
    lineHeight: 1.65,
    margin: '0 0 32px',
    textAlign: 'center' as const,
  },
  btnPrimary: {
    background: '#0A66C2',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 36px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.1px',
    transition: 'background 0.15s',
  },
};

function BlockedScreen({ title, message, icon }: {
  title: string;
  message: string;
  icon?: React.ReactNode;
}) {
  return (
    <div style={styles.page}>
      <div style={styles.leftPanel}>
        <img src="/app-logo.png" alt="JobExcello" style={styles.logoImg} />
        <div>
          <p style={styles.leftTagline}>Video Interviews</p>
          <p style={styles.leftHeading}>Smarter hiring starts here.</p>
          <p style={styles.leftBody}>
            AI-assisted interviews, real-time evaluation, and seamless candidate experience — all in one place.
          </p>
        </div>
        <p style={styles.leftFooter}>Powered by HireON AI™</p>
      </div>

      <div style={styles.rightPanel}>
        <div style={styles.contentWrap}>
          <div style={styles.iconCircle}>
            {icon ?? (
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            )}
          </div>
          <p style={styles.heading}>{title}</p>
          <p style={styles.subtext}>{message}</p>
          <button
            style={styles.btnPrimary}
            onMouseEnter={e => (e.currentTarget.style.background = '#0958a8')}
            onMouseLeave={e => (e.currentTarget.style.background = '#0A66C2')}
            onClick={() => { window.location.href = 'https://www.jobexcello.com'; }}
          >
            Go to JobExcello
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PreJoinScreens() {
  const { user } = useAppState();
  const { getAudioAndVideoTracks } = useVideoContext();
  const { URLRoomName } = useParams<{ URLRoomName?: string }>();
  const [step, setStep] = useState(Steps.roomNameStep);
  const [name, setName] = useState<string>(user?.displayName || '');
  const [roomName, setRoomName] = useState<string>('');
  const [mediaError, setMediaError] = useState<Error>();

  const params = new URLSearchParams(window.location.search);
  const isNewFlow = !!params.get('role') && !!params.get('join_token');
  const urlToken = params.get('token');
  const urlIdentity = params.get('identity');

  useEffect(() => {
    if (URLRoomName) {
      setRoomName(URLRoomName);
      if (isNewFlow && urlToken && urlIdentity) {
        setName(urlIdentity);
        setStep(Steps.deviceSelectionStep);
      } else if (!isNewFlow && user?.displayName) {
        setStep(Steps.deviceSelectionStep);
      }
    }
  }, [user, URLRoomName]);

  useEffect(() => {
    if (step === Steps.deviceSelectionStep && !mediaError) {
      getAudioAndVideoTracks().catch(error => {
        console.log('Error acquiring local media:');
        console.dir(error);
        setMediaError(error);
      });
    }
  }, [getAudioAndVideoTracks, step, mediaError]);

  if (URLRoomName && !isNewFlow) {
    return (
      <BlockedScreen
        title="Access Denied"
        message="Direct room access is not allowed. Please use your interview invitation link to join."
      />
    );
  }

  if (!URLRoomName && !isNewFlow) {
    return (
      <BlockedScreen
        title="No Interview Session"
        message="Please use your interview invitation link to join a session."
      />
    );
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // @ts-ignore
    if (!window.location.origin.includes('twil.io') && !window.STORYBOOK_ENV) {
      window.history.replaceState(null, '', window.encodeURI(`/room/${roomName}${window.location.search || ''}`));
    }
    setStep(Steps.deviceSelectionStep);
  };

  return (
    <IntroContainer>
      <MediaErrorSnackbar error={mediaError} />
      {step === Steps.roomNameStep && (
        <RoomNameScreen
          name={name}
          roomName={roomName}
          setName={setName}
          setRoomName={setRoomName}
          handleSubmit={handleSubmit}
        />
      )}
      {step === Steps.deviceSelectionStep && (
        <DeviceSelectionScreen name={name} roomName={roomName} setStep={setStep} />
      )}
    </IntroContainer>
  );
}